import {
  SKILL_CATEGORIES,
  SKILL_CATEGORY_LABELS,
  fmtRecent,
  type SkillCategory,
} from '@remote-medium/shared';
import {
  Badge,
  Button,
  Input,
  SearchableSelect,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import type { InferResponseType } from 'hono/client';
import { useState } from 'react';

import { admin } from '../../../lib/client.ts';
import { DataTable, EmptyRow, Notice, StatusRow, tableReady } from '../../../lib/ui.tsx';
import { RejectInUse } from './RejectInUse.tsx';

type Pending = InferResponseType<(typeof admin)['skills']['pending']['$get'], 200>['data'][number];
type Taxonomy = InferResponseType<(typeof admin.taxonomy)['$get'], 200>['data'];

const CATEGORY_OPTIONS = SKILL_CATEGORIES.map((c) => ({
  value: c,
  label: SKILL_CATEGORY_LABELS[c],
}));

// Skills companies and candidates added themselves (Q79). They are in use
// already: keep one, move its users to an existing skill, or reject it (it is
// removed from every role and profile that used it). Rejecting one in use asks
// for a replacement first. Every review emails everyone using it (author,
// candidates, company users), so the note is written for all of them.
export function PendingSkills({ onReviewed }: { onReviewed: () => void }) {
  const { data, error, loading, reload } = useApi(() => unwrap(admin.skills.pending.$get()), []);
  const { data: taxonomy, reload: reloadTaxonomy } = useApi(
    () => unwrap(admin.taxonomy.$get()),
    [],
  );
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const ready = tableReady({ data, loading, error });
  const rows = ready ? (data ?? []) : [];
  const approved = (taxonomy?.skills ?? []).filter((s) => s.status === 'approved');

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setMsg(null);
    try {
      await fn();
      setMsg({ tone: 'info', text: ok });
      reload();
      reloadTaxonomy();
      onReviewed();
    } catch (e) {
      reloadTaxonomy();
      setMsg({ tone: 'error', text: errorMessage(e) });
    }
  };

  return (
    <>
      <p className="m-0 max-w-[65ch] text-sm text-muted-foreground">
        Added by companies and candidates when nothing fitted, and already in use by them. Keep the
        real ones, move typos and synonyms to an existing skill, and reject the rest (it is then
        removed from their roles and profiles). Every review emails everyone using it, not just the
        person who added it.
      </p>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <DataTable minWidth="min-w-3xl">
        <thead>
          <tr>
            <th>Skill</th>
            <th>Added by</th>
            <th>In use</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={4} loading={loading} error={error} />
          {ready && rows.length === 0 && (
            <EmptyRow colSpan={4}>Nothing to review. New skills show up here.</EmptyRow>
          )}
          {rows.map((s) => (
            <tr key={s.id} className="align-top hover:bg-muted/50">
              <td className="font-semibold">{s.name}</td>
              <td>
                <div className="flex flex-col gap-1 text-sm">
                  {s.suggestedBy?.email ? (
                    <>
                      <Badge variant="outline" className="w-fit">
                        {s.suggestedBy.role === 'company_user' ? 'Company' : 'Candidate'}
                      </Badge>
                      <span className="break-all">{s.suggestedBy.email}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">Account deleted</span>
                  )}
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {fmtRecent(s.createdAt)}
                  </span>
                </div>
              </td>
              <td className="text-sm whitespace-nowrap tabular-nums">
                {s.roleCount} {s.roleCount === 1 ? 'role' : 'roles'}
                <br />
                {s.candidateCount} {s.candidateCount === 1 ? 'candidate' : 'candidates'}
              </td>
              <td>
                <Review
                  skill={s}
                  approved={approved}
                  onKeep={(edits) =>
                    act(
                      () =>
                        unwrap(
                          admin.skills[':id'].approve.$post({ param: { id: s.id }, json: edits }),
                        ),
                      `Kept “${edits.name}” in ${SKILL_CATEGORY_LABELS[edits.category]}.`,
                    )
                  }
                  onReplace={(intoId, note) =>
                    act(
                      () =>
                        unwrap(
                          admin.skills[':id'].replace.$post({
                            param: { id: s.id },
                            json: { intoId, note },
                          }),
                        ),
                      `Moved “${s.name}” to ${approved.find((a) => a.id === intoId)?.name}.`,
                    )
                  }
                  onAddAndReplace={(added, note) =>
                    act(async () => {
                      const created = await unwrap(admin.taxonomy.skills.$post({ json: added }));
                      await unwrap(
                        admin.skills[':id'].replace.$post({
                          param: { id: s.id },
                          json: { intoId: created.id, note },
                        }),
                      );
                    }, `Added “${added.name}” and moved “${s.name}” to it.`)
                  }
                  onReject={(note) =>
                    act(
                      () =>
                        unwrap(
                          admin.skills[':id'].reject.$post({ param: { id: s.id }, json: { note } }),
                        ),
                      `Rejected “${s.name}”.`,
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </>
  );
}

function Review({
  skill,
  approved,
  onKeep,
  onReplace,
  onAddAndReplace,
  onReject,
}: {
  skill: Pending;
  approved: Taxonomy['skills'];
  onKeep: (edits: { name: string; category: SkillCategory }) => Promise<void>;
  onReplace: (intoId: string, note: string | null) => Promise<void>;
  onAddAndReplace: (
    added: { name: string; category: SkillCategory },
    note: string | null,
  ) => Promise<void>;
  onReject: (note: string | null) => Promise<void>;
}) {
  const [name, setName] = useState(skill.name);
  const [category, setCategory] = useState<SkillCategory>('tool');
  const [confirming, setConfirming] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<SkillCategory>('tool');
  const inUse = skill.roleCount + skill.candidateCount > 0;
  const [intoId, setIntoId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // One action at a time: a second click would hit an already-reviewed row.
  const once = (fn: () => Promise<void>) => {
    setBusy(true);
    void fn().finally(() => setBusy(false));
  };
  const stem = skill.name.toLowerCase().slice(0, 4);
  const similar = new Set(
    approved.filter((k) => k.name.toLowerCase().includes(stem)).map((k) => k.id),
  );
  const options = [
    ...approved
      .filter((k) => similar.has(k.id))
      .map((k) => ({ value: k.id, label: k.name, group: 'Similar' })),
    ...approved
      .filter((k) => !similar.has(k.id))
      .map((k) => ({ value: k.id, label: k.name, group: 'All skills' })),
  ];
  const trimmedNote = note.trim() || null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={`Name for ${skill.name}`}
          className="w-44"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="w-44">
          <SearchableSelect
            aria-label={`Category for ${skill.name}`}
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(v) => v && setCategory(v as SkillCategory)}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !name.trim()}
          onClick={() => once(() => onKeep({ name: name.trim(), category }))}
        >
          Keep
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-56">
          <SearchableSelect
            aria-label={`Replace ${skill.name} with an existing skill`}
            placeholder="Replace with existing…"
            options={options}
            value={intoId}
            onChange={setIntoId}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !intoId}
          onClick={() => intoId && once(() => onReplace(intoId, trimmedNote))}
        >
          Replace
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => (inUse ? setConfirming(true) : once(() => onReject(trimmedNote)))}
        >
          Reject
        </Button>
      </div>
      {confirming && (
        <RejectInUse
          name={skill.name}
          roleCount={skill.roleCount}
          candidateCount={skill.candidateCount}
          consequence="Rejecting removes it from all of them."
          busy={busy}
          canAdd={newName.trim().length > 0}
          addFields={
            <>
              <Input
                aria-label={`New skill to replace ${skill.name}`}
                placeholder="New skill name"
                className="w-44"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <div className="w-44">
                <SearchableSelect
                  aria-label={`Category for the new skill`}
                  options={CATEGORY_OPTIONS}
                  value={newCategory}
                  onChange={(v) => v && setNewCategory(v as SkillCategory)}
                />
              </div>
            </>
          }
          onAdd={() =>
            once(() =>
              onAddAndReplace({ name: newName.trim(), category: newCategory }, trimmedNote),
            )
          }
          onRejectAnyway={() => once(() => onReject(trimmedNote))}
          onCancel={() => setConfirming(false)}
        />
      )}
      <Input
        aria-label={`Note to everyone using ${skill.name}`}
        placeholder="Note to everyone using it (optional)"
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </div>
  );
}
